import { BaseFormFieldSection } from "@app/components/shared/BaseFormFieldSection";
import { useFormatErrorDescription } from "@app/hooks/useFormatErrorDescription";
import { useRequestUpgrade } from "@app/lib/swr/upgrade_requests";
import { MAX_UPGRADE_REQUEST_REASON_LENGTH_CHARS } from "@app/types/memberships";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Hoverable,
  TextArea,
} from "@dust-tt/sparkle";
import { zodResolver } from "@hookform/resolvers/zod";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

function getRequestUpgradeFormSchema(
  requireReason: boolean,
  t: (descriptor: MessageDescriptor) => string
) {
  return z.object({
    reason: requireReason
      ? z
          .string()
          .trim()
          .min(1, t(msg`A reason is required to submit an upgrade request.`))
          .max(MAX_UPGRADE_REQUEST_REASON_LENGTH_CHARS)
      : z.string().trim().max(MAX_UPGRADE_REQUEST_REASON_LENGTH_CHARS),
  });
}

type RequestUpgradeFormValues = z.infer<
  ReturnType<typeof getRequestUpgradeFormSchema>
>;

type UsageUpgradeButtonVariant = "link" | "button";

interface UsageUpgradeButtonProps {
  owner: LightWorkspaceType;
  hasPendingUpgradeRequest: boolean;
  variant?: UsageUpgradeButtonVariant;
  isManager?: boolean;
  requireReason?: boolean;
  onManagerNavigate?: () => void;
}

// Member-initiated upgrade-request CTA. Opens a confirmation dialog and posts
// the request via `useRequestUpgrade`. Rendered either as an inline link (usage
// banner) or as a primary button (personal settings) through `variant`.
export function UsageUpgradeButton({
  owner,
  hasPendingUpgradeRequest,
  variant = "link",
  isManager = false,
  requireReason = false,
  onManagerNavigate,
}: UsageUpgradeButtonProps) {
  const { t } = useLingui();
  const { doRequestUpgrade } = useRequestUpgrade({ workspaceId: owner.sId });
  const formatErrorDescription = useFormatErrorDescription();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [requested, setRequested] = useState(false);

  const requestUpgradeFormSchema = useMemo(
    () => getRequestUpgradeFormSchema(requireReason, t),
    [requireReason, t]
  );
  const form = useForm<RequestUpgradeFormValues>({
    resolver: zodResolver(requestUpgradeFormSchema),
    defaultValues: { reason: "" },
  });

  const alreadyRequested = hasPendingUpgradeRequest || requested;

  const onSubmit = async (values: RequestUpgradeFormValues) => {
    const trimmedReason = values.reason.trim();
    const result = await doRequestUpgrade({
      reason: trimmedReason.length > 0 ? trimmedReason : undefined,
    });
    if (result.isOk()) {
      setRequested(true);
      setIsDialogOpen(false);
      form.reset();
      return;
    }
    if (result.error.errorType === "invalid_request_error") {
      form.setError("reason", {
        type: "manual",
        message: formatErrorDescription(result.error.error),
      });
    }
  };

  function renderTrigger() {
    if (isManager) {
      const usageHref = `/w/${owner.sId}/credits`;

      if (variant === "button") {
        return (
          <Button
            variant="primary"
            size="xs"
            label={t`Go to workspace usage`}
            href={usageHref}
            onClick={onManagerNavigate}
          />
        );
      }

      return (
        <Hoverable
          variant="primary"
          className="copy-sm underline underline-offset-2"
          href={usageHref}
          onClick={onManagerNavigate}
        >
          <Trans>Go to workspace usage</Trans>
        </Hoverable>
      );
    }

    if (variant === "button") {
      return (
        <Button
          variant="primary"
          size="xs"
          label={alreadyRequested ? t`Requested` : t`Request for upgrade`}
          disabled={alreadyRequested}
          onClick={() => setIsDialogOpen(true)}
        />
      );
    }

    if (alreadyRequested) {
      return (
        <span className="copy-sm text-muted-foreground">
          <Trans>Request sent</Trans>
        </span>
      );
    }

    return (
      <Hoverable
        variant="primary"
        className="copy-sm underline underline-offset-2"
        onClick={() => setIsDialogOpen(true)}
      >
        <Trans>Request an upgrade</Trans>
      </Hoverable>
    );
  }

  return (
    <>
      {renderTrigger()}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => !open && setIsDialogOpen(false)}
      >
        <DialogContent size="md">
          <FormProvider {...form}>
            <DialogHeader>
              <DialogTitle>
                <Trans>Request a usage limit upgrade</Trans>
              </DialogTitle>
            </DialogHeader>
            <DialogContainer>
              <div className="flex flex-col gap-4">
                <p className="text-sm text-muted-foreground">
                  <Trans>
                    Your workspace admins and managers will review this request.
                  </Trans>
                </p>
                <BaseFormFieldSection<HTMLTextAreaElement>
                  fieldName="reason"
                  title={
                    requireReason
                      ? t`Help your admin decide (required)`
                      : t`Help your admin decide`
                  }
                >
                  {({
                    registerRef,
                    registerProps,
                    onChange,
                    errorMessage,
                    fieldState,
                  }) => {
                    const showError =
                      fieldState.isDirty ||
                      fieldState.isTouched ||
                      form.formState.isSubmitted;

                    return (
                      <TextArea
                        ref={registerRef}
                        placeholder={t`e.g. processing a large batch of documents for this quarter's audit`}
                        rows={3}
                        showErrorLabel={showError}
                        error={showError ? errorMessage : undefined}
                        onChange={onChange}
                        {...registerProps}
                      />
                    );
                  }}
                </BaseFormFieldSection>
              </div>
            </DialogContainer>
            <DialogFooter
              leftButtonProps={{
                label: t`Cancel`,
                variant: "outline",
                onClick: () => setIsDialogOpen(false),
              }}
              rightButtonProps={{
                label: t`Send request`,
                variant: "primary",
                isLoading: form.formState.isSubmitting,
                disabled: form.formState.isSubmitting,
                onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  void form.handleSubmit(onSubmit)();
                },
              }}
            />
          </FormProvider>
        </DialogContent>
      </Dialog>
    </>
  );
}
